import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, FileText, Calendar, DollarSign, Clock, ShieldCheck, Factory, Cpu, Link as LinkIcon } from 'lucide-react';
import { useFactory } from '../../../../context/FactoryContext';
import { PlansHierarchy } from '../../../mantenimiento/components/plans/PlansHierarchy';

export const ExtContractModal = ({ isOpen, onClose, onSave, contract = null, company }) => {
  const isEdit = !!contract;

  const [formData, setFormData] = useState({
    contract_number: '',
    contract_type: 'preventive',
    service_type: '',
    start_date: '',
    end_date: '',
    annual_cost: '',
    payment_terms: '',
    response_time: '',
    service_hours: '24/7',
    asset_id: '',
    factory_id: '',
    included_services: '',
    excluded_services: '',
    document_url: '',
    active: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Asset hierarchy selector states
  const [isHierarchyOpen, setHierarchyOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [selectedAssetDetails, setSelectedAssetDetails] = useState(null);

  const { selectedFactory, factories, factoryCodes } = useFactory();
  const currentFactory = selectedFactory ? factories.find(f => f.asset_code === selectedFactory || f.name === selectedFactory) : null;

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      return dateString.slice(0, 10);
    } catch {
      return '';
    }
  };
  console.log(Number(formData.factory_id))
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setFormErrors({});
      if (contract) {
        setFormData({
          contract_number: contract.contract_number || '',
          contract_type: contract.contract_type || 'preventive',
          service_type: contract.service_type || '',
          start_date: formatDateForInput(contract.start_date),
          end_date: formatDateForInput(contract.end_date),
          annual_cost: contract.annual_cost !== undefined && contract.annual_cost !== null ? String(contract.annual_cost) : '',
          payment_terms: contract.payment_terms || '',
          response_time: contract.response_time !== undefined && contract.response_time !== null ? String(contract.response_time) : '',
          service_hours: contract.service_hours || '24/7',
          asset_id: contract.asset_id !== undefined && contract.asset_id !== null ? String(contract.asset_id) : '',
          factory_id: contract.factory_id !== undefined && contract.factory_id !== null ? String(contract.factory_id) : '',
          included_services: contract.included_services || '',
          excluded_services: contract.excluded_services || '',
          document_url: contract.document_url || '',
          active: contract.active !== undefined ? contract.active : true,
        });
        setSelectedAsset(contract.asset_id ? Number(contract.asset_id) : null);
      } else {
        setFormData({
          contract_number: '',
          contract_type: 'preventive',
          service_type: '',
          start_date: '',
          end_date: '',
          annual_cost: '',
          payment_terms: '',
          response_time: '',
          service_hours: '24/7',
          asset_id: '',
          factory_id: currentFactory?.id || currentFactory?.asset_id || '',
          included_services: '',
          excluded_services: '',
          document_url: '',
          active: true,
        });
        setSelectedAsset(null);
        setSelectedAssetDetails(null);
      }
      setHierarchyOpen(false);
    }
  }, [isOpen, contract]);

  // Sync selected asset from PlansHierarchy to formData
  useEffect(() => {
    if (selectedAssetDetails?.ID) {
      setFormData((prev) => ({
        ...prev,
        asset_id: selectedAssetDetails.ID.toString(),
      }));
    }
  }, [selectedAssetDetails]);

  const handleClearAsset = () => {
    setSelectedAsset(null);
    setSelectedAssetDetails(null);
    setFormData((prev) => ({
      ...prev,
      asset_id: '',
    }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.contract_number.trim()) {
      errors.contract_number = 'Contract Number is required';
    }
    if (!company?.id) {
      errors.company = 'Company ID is required';
    }
    if (!formData.contract_type) {
      errors.contract_type = 'Contract Type is required';
    }
    if (!formData.service_type.trim()) {
      errors.service_type = 'Service Type is required';
    }
    if (!formData.start_date) {
      errors.start_date = 'Start Date is required';
    }
    if (!formData.end_date) {
      errors.end_date = 'End Date is required';
    }
    if (formData.start_date && formData.end_date && formData.end_date <= formData.start_date) {
      errors.end_date = 'End Date must be after Start Date';
    }
    if (formData.response_time !== '' && !isNaN(formData.response_time)) {
      const respTime = Number(formData.response_time);
      if (respTime < 0 || respTime > 2147483647) {
        errors.response_time = 'Response time must be between 0 and 2,147,483,647 hours';
      }
    }
    if (formData.annual_cost !== '' && !isNaN(formData.annual_cost)) {
      const cost = Number(formData.annual_cost);
      if (cost < 0 || cost > 9999999999999.99) {
        errors.annual_cost = 'Annual cost must be between 0 and 9,999,999,999,999.99 €';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    if (name === 'factory_id') {
      handleClearAsset();
      if (!value) {
        setHierarchyOpen(false);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const payload = {
        contract_number: formData.contract_number.trim(),
        company_id: company.id,
        contract_type: formData.contract_type,
        service_type: formData.service_type.trim(),
        start_date: formData.start_date ? `${formData.start_date}T00:00:00Z` : undefined,
        end_date: formData.end_date ? `${formData.end_date}T23:59:59Z` : undefined,
        annual_cost: formData.annual_cost !== '' && !isNaN(formData.annual_cost)
          ? Math.round(parseFloat(formData.annual_cost) * 100) / 100
          : 0,
        payment_terms: formData.payment_terms.trim(),
        response_time: formData.response_time !== '' ? parseInt(formData.response_time, 10) : 0,
        service_hours: formData.service_hours.trim(),
        asset_id: formData.asset_id ? Number(formData.asset_id) : null,
        factory_id: formData.factory_id ? Number(formData.factory_id) : null,
        included_services: formData.included_services.trim(),
        excluded_services: formData.excluded_services.trim(),
        document_url: formData.document_url.trim(),
        active: isEdit ? formData.active : true,
      };

      await onSave(payload);
      onClose();
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      // 1. Duplicate Contract Number
      if (
        msg.includes('duplicate key') ||
        msg.includes('idx_contracts_number') ||
        msg.includes('already exists') ||
        err?.status === 500
      ) {
        setFormErrors((prev) => ({
          ...prev,
          contract_number: 'A contract with this number already exists. Please choose a different number.',
        }));

      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Asset hierarchy modal (if open and factory selected) */}
      <div className={`${isHierarchyOpen && formData.factory_id ? 'visible' : 'invisible'} absolute left-1/5 -translate-x-1/2`}>
        <PlansHierarchy
          key={formData.factory_id || 'all'}
          assetId={selectedAsset}
          setAssetId={setSelectedAsset}
          setIsLoadingAsset={setIsLoadingAsset}
          setAssetDetails={setSelectedAssetDetails}
          selectMode="asset"
          rootAssetId={formData.factory_id ? Number(formData.factory_id) : -1}
        />
      </div>

      {/* Main modal */}
      <div
        className={`bg-gray-800 ${!isHierarchyOpen ? 'absolute left-1/2 -translate-x-1/2' : 'absolute left-1/2 -translate-x-1/2'
          } w-full max-w-2xl rounded-2xl border border-gray-700 shadow-2xl max-h-[90vh] flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">
              {isEdit ? `Edit Contract: ${contract.contract_number}` : 'New Maintenance Contract'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-0">
            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Row 1: Contract Number & Contract Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contract Number *
                </label>
                <input
                  type="text"
                  name="contract_number"
                  value={formData.contract_number}
                  onChange={handleChange}
                  maxLength={100}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.contract_number ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                  placeholder="e.g. CONT-2025-001"
                />
                {formErrors.contract_number && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.contract_number}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contract Type *
                </label>
                <select
                  name="contract_type"
                  value={formData.contract_type}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="preventive">Preventive</option>
                  <option value="corrective">Corrective</option>
                  <option value="full_service">Full Service</option>
                </select>
                {formErrors.contract_type && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.contract_type}</p>
                )}
              </div>
            </div>

            {/* Row 2: Service Type & Service Hours */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Service Type *
                </label>
                <input
                  type="text"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  maxLength={100}
                  list="service-type-options"
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.service_type ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                  placeholder="e.g. aire_comprimido, refrigeracion, electricidad"
                />
                <datalist id="service-type-options">
                  <option value="aire_comprimido">Aire Comprimido</option>
                  <option value="refrigeracion">Refrigeración / Climatización</option>
                  <option value="electricidad">Electricidad / Alta Tensión</option>
                  <option value="mecanica">Mecánica Industrial</option>
                  <option value="calibracion">Calibración e Instrumentación</option>
                  <option value="fontaneria">Fontanería y Fluidos</option>
                  <option value="limpieza_industrial">Limpieza Técnica</option>
                </datalist>
                {formErrors.service_type && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.service_type}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Service Hours / Coverage
                </label>
                <input
                  type="text"
                  name="service_hours"
                  value={formData.service_hours}
                  onChange={handleChange}
                  maxLength={100}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 24/7, Laborables 8-18h"
                />
              </div>
            </div>

            {/* Row 3: Validity Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.start_date ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                />
                {formErrors.start_date && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.start_date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.end_date ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                />
                {formErrors.end_date && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.end_date}</p>
                )}
              </div>
            </div>

            {/* Row 4: Annual Cost & Response Time & Payment Terms */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Annual Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="9999999999999.99"
                  name="annual_cost"
                  value={formData.annual_cost}
                  onChange={handleChange}
                  onBlur={() => {
                    if (formData.annual_cost !== '' && !isNaN(formData.annual_cost)) {
                      setFormData((prev) => ({
                        ...prev,
                        annual_cost: (Math.round(parseFloat(prev.annual_cost) * 100) / 100).toFixed(2),
                      }));
                    }
                  }}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 12000.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Response Time (Hours)
                </label>
                <input
                  type="number"
                  min="0"
                  max="2147483647"
                  step="1"
                  name="response_time"
                  value={formData.response_time}
                  onChange={handleChange}
                  className={`w-full px-3.5 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.response_time ? 'border-red-500 border-2' : 'border-gray-600'
                    }`}
                  placeholder="e.g. 48"
                />
                {formErrors.response_time && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.response_time}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Payment Terms
                </label>
                <input
                  type="text"
                  name="payment_terms"
                  value={formData.payment_terms}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 30 días fecha factura"
                />
              </div>
            </div>

            {/* Row 5: Associated Factory & Asset */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Factory (Optional)
                </label>
                <select
                  name="factory_id"
                  value={formData.factory_id}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None</option>
                  {company?.factories?.map((factory_id) => (
                    <option key={factory_id} value={factory_id}>
                      {factories.find(f => f.id === factory_id)?.name || factory_id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Specific Asset (Optional)
                </label>
                <div className="flex flex-row gap-2">
                  <div
                    className={`w-full px-3.5 py-2 rounded-lg border flex items-center justify-between text-sm overflow-hidden ${
                      !formData.factory_id
                        ? 'bg-gray-800/60 border-gray-700 text-gray-500 cursor-not-allowed select-none'
                        : `bg-gray-700 border-gray-600 ${formData.asset_id ? 'text-white' : 'text-gray-400'}`
                    }`}
                  >
                    <span className="truncate">
                      {!formData.factory_id
                        ? 'Select factory first'
                        : selectedAssetDetails
                          ? `${selectedAssetDetails.Location || ''}${selectedAssetDetails.Code ? ` - ${selectedAssetDetails.Code}` : ''}`
                          : isLoadingAsset
                            ? <Loader2 className="w-4 h-4 animate-spin inline" />
                            : formData.asset_id
                              ? `Asset #${formData.asset_id}`
                              : 'General / All Assets'}
                    </span>
                    {formData.asset_id && formData.factory_id && (
                      <button
                        type="button"
                        onClick={handleClearAsset}
                        className="text-xs text-red-400 hover:text-red-300 ml-2 shrink-0"
                        title="Clear asset selection"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={!formData.factory_id}
                    onClick={() => setHierarchyOpen(!isHierarchyOpen)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors shrink-0 ${
                      !formData.factory_id
                        ? 'border-gray-700 bg-gray-800/60 text-gray-500 cursor-not-allowed'
                        : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {isHierarchyOpen ? 'Close' : 'Select'}
                  </button>
                </div>
              </div>
            </div>

            {/* Row 6: Document URL */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Document URL / Link
              </label>
              <input
                type="text"
                name="document_url"
                value={formData.document_url}
                onChange={handleChange}
                maxLength={500}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://storage.example.com/contracts/cont-2025.pdf"
              />
            </div> */}

            {/* Row 7: Included & Excluded Services */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Included Services
                </label>
                <textarea
                  name="included_services"
                  value={formData.included_services}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g. 4 revisiones preventivas anuales, cambio de filtros y aceite, mano de obra incluida..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Excluded Services
                </label>
                <textarea
                  name="excluded_services"
                  value={formData.excluded_services}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g. Repuestos extraordinarios, intervenciones fuera de horario laboral..."
                />
              </div>
            </div>

            {/* Active Switch - Only on Edit Modal */}
            {isEdit && (
              <div className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  id="contract-active"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                />
                <label htmlFor="contract-active" className="text-sm font-medium text-gray-300 cursor-pointer">
                  Contract Active
                </label>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors border border-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Contract'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
